import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Group } from '@klickdata/core/group';
import { AccessControlPermission } from '@klickdata/core/resource/src/resource.model';
import { User } from '@klickdata/core/user';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-resource-permissions-handler',
    templateUrl: './resource-permissions-handler.component.html',
    styleUrls: ['./resource-permissions-handler.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourcePermissionsHandlerComponent extends OnDestroyHandler implements OnInit {
    @Input() hasEditPrivilege: boolean;
    @Input() permissionsFormArray: FormArray;
    @Input() user: User;
    @Input() group: Group;
    @Input() permissions: AccessControlPermission[];
    @Input() resourcePermissions: {
        index: number;
        value: string;
        label: string;
        tooltip?: string;
        disableUser?: boolean;
    }[];
    public permForm: FormGroup;
    public toolTipsMapping: { value: true | false | null; label: string }[] = [
        { value: true, label: $localize`:@@permissionGranted:Permission granted` },
        { value: false, label: $localize`:@@permissionDenied:Permission denied` },
        { value: null, label: $localize`:@@permissionInherited:Permission inherited from higher level` },
    ];
    constructor(protected fb: FormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.permForm = this.fb.group({
            id: [this.user?.id ?? this.group?.id],
            permissions: this.fb.array([]),
        });
        this.handlePermissions();
    }
    handlePermissions() {
        if (!!this.permissions?.length) {
            this.permissions.forEach((perm) =>
                (this.permForm.get('permissions') as FormArray).push(this.addNewPerm(perm))
            );
        }
        this.permForm.markAsPristine();

        this.permForm.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter(() => this.permForm.dirty || this.permFormArray.dirty)
            )
            .subscribe(() => {
                this.pushIntoPermForm();
            });
    }

    private pushIntoPermForm() {
        const existingIndex = this.permissionsFormArray.controls.findIndex(
            (group: FormGroup) => group.getRawValue().id === this.permForm.value.id
        );
        if (existingIndex !== -1) {
            this.permissionsFormArray.at(existingIndex).patchValue(this.permForm.value, { emitEvent: false });
        } else {
            this.permissionsFormArray.push(this.permForm, { emitEvent: false });
        }
    }

    getDirtyPermForm(): FormGroup {
        const dirtyPermissions = this.permForm.get('permissions') as FormArray;
        const newPermissionsArray = this.fb.array([] as FormGroup[]);

        dirtyPermissions.controls.forEach((control) => {
            if (control.dirty) {
                newPermissionsArray.push(
                    this.fb.group({
                        permission_type: [control.get('permission_type').value],
                        permission_value: [control.get('permission_value').value],
                    })
                );
            }
        });
        return this.fb.group({
            id: [this.permForm.get('id').value],
            permissions: newPermissionsArray,
        });
    }

    addNewPerm(permission: AccessControlPermission) {
        const group = this.fb.group({
            permission_type: [permission.permission_type],
            permission_value: [permission.permission_value],
        });
        if (permission.disabled) {
            group.get('permission_value').disable();
        }
        return group;
    }
    public toggleAll(status: boolean, modelId: number) {
        if (this.permForm.value.id == modelId) {
            this.permFormArray.controls
                .filter((control) => control.get('permission_value').enabled)
                .forEach((ctr) => {
                    ctr.get('permission_value').patchValue(status);
                    ctr.markAsDirty();
                });
            this.pushIntoPermForm();
        }
    }
    public checkAllValues() {
        const controls = this.permFormArray.controls.filter((control) => control.get('permission_value').enabled);
        const allTrue = controls.every((group) => group.get('permission_value')?.value === true);
        const allFalse = controls.every((group) => group.get('permission_value')?.value === false);
        if (allFalse) {
            this.handleModelMasterToggle(false);
        }
        if (allTrue) {
            this.handleModelMasterToggle(true);
        }
        if (!allFalse && !allTrue) {
            this.handleModelMasterToggle();
        }
    }
    get permFormArray(): FormArray {
        return this.permForm?.get('permissions') as FormArray;
    }
    handleModelMasterToggle(status?: boolean) {
        if (status) {
            if (this.user) {
                this.user.all_resource_perm_checked = status;
                this.user.some_resource_perm_checked = false;
            }
            if (this.group) {
                this.group.all_resource_perm_checked = status;
                this.group.some_resource_perm_checked = false;
            }
        } else {
            if (this.user) {
                this.user.some_resource_perm_checked = true;
            }
            if (this.group) {
                this.group.some_resource_perm_checked = true;
            }
        }
    }
}
